/**
 * D1-10: on-device quality gates for goods-in capture (blur / glare / resolution).
 * Mirror of backend/receiving/goods_in_capture.assess_image_quality defaults.
 */

export const MIN_WIDTH = 640;
export const MIN_HEIGHT = 480;
export const MIN_SHARPNESS = 40;
export const MAX_GLARE_RATIO = 0.35;

/**
 * @param {{ width: number, height: number, sharpness: number, glareRatio: number }} metrics
 * @returns {{ accepted: boolean, reasons: string[], metrics: object }}
 */
export function assessQuality({ width, height, sharpness, glareRatio }) {
  const reasons = [];
  if (width < MIN_WIDTH || height < MIN_HEIGHT) reasons.push('resolution_too_low');
  if (sharpness < MIN_SHARPNESS) reasons.push('blur');
  if (glareRatio > MAX_GLARE_RATIO) reasons.push('glare');
  return {
    accepted: reasons.length === 0,
    reasons,
    metrics: { width, height, sharpness, glareRatio },
  };
}

/**
 * Laplacian-variance proxy from ImageData (RGBA).
 * @param {ImageData} imageData
 */
export function estimateSharpness(imageData) {
  const { data, width, height } = imageData;
  if (width < 3 || height < 3) return 0;
  let sum = 0;
  let sumSq = 0;
  let n = 0;
  const gray = (i) => 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
  for (let y = 1; y < height - 1; y += 2) {
    for (let x = 1; x < width - 1; x += 2) {
      const i = (y * width + x) * 4;
      const c = gray(i);
      const lap =
        -c +
        gray(i - 4) +
        gray(i + 4) +
        gray(i - width * 4) +
        gray(i + width * 4);
      sum += lap;
      sumSq += lap * lap;
      n += 1;
    }
  }
  if (n === 0) return 0;
  const mean = sum / n;
  return sumSq / n - mean * mean;
}

/**
 * Fraction of near-white pixels (glare proxy).
 * @param {ImageData} imageData
 */
export function estimateGlareRatio(imageData) {
  const { data } = imageData;
  let bright = 0;
  const pixels = data.length / 4;
  for (let i = 0; i < data.length; i += 4) {
    if (data[i] > 245 && data[i + 1] > 245 && data[i + 2] > 245) bright += 1;
  }
  return pixels === 0 ? 0 : bright / pixels;
}
